import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { Button } from "./ui/button";
import { Check, Diamond, Rocket, Crown, Zap, HelpCircle, Flame, Moon, ArrowRight } from "lucide-react";
import { SiSolana } from "react-icons/si";
import { useState } from "react";
import { useLicense } from "./LicenseProvider";
import { useCart } from "@/context/CartContext";

interface PricingPlanProps {
  name: string;
  price: number;
  description: string;
  features: {
    text: string;
    included: boolean;
    highlight?: boolean;
  }[];
  isPopular?: boolean;
  isTrial?: boolean;
  icon: React.ReactNode;
  color: string;
  delay: number;
}

interface CartModalProps {
  open: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

function CartModal({ open, onClose, onCheckout }: CartModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#101c16] rounded-2xl p-8 shadow-2xl max-w-md w-full border border-[#14F195]/30 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl">&times;</button>
        <h2 className="text-2xl font-bold mb-4 text-center text-white">Your Cart</h2>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-semibold text-white">Solana Volume Bot Free Trial</div>
            <div className="text-xs text-gray-400">7 days, full access</div>
          </div>
          <div className="flex items-center gap-1">
            <SiSolana className="text-[#00FFA3] w-6 h-6" />
            <span className="font-bold text-green-400 text-lg">0</span>
            <span className="line-through text-gray-400 text-base ml-1">9</span>
            <span className="text-[#00FFA3] font-bold ml-1">SOL</span>
          </div>
        </div>
        <ul className="mb-6 space-y-1 text-white text-sm">
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#14F195]" /> 7 days, full access</li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#14F195]" /> All DEX integrations</li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#14F195]" /> Priority support</li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#14F195]" /> Cancel anytime</li>
        </ul>
        <button onClick={onCheckout} className="w-full bg-gradient-to-r from-[#9945FF] to-[#14F195] text-white py-3 rounded-xl font-bold text-lg mt-2 shadow-lg hover:opacity-90 transition">Proceed to Registration</button>
        <div className="text-xs text-gray-400 mt-4 text-center flex items-center justify-center gap-1"><span className="inline-block"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path fill="#14F195" d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm-1-7V7h2v6h-2zm0 4v-2h2v2h-2z"/></svg></span>No payment required. Secure checkout.</div>
      </div>
    </div>
  );
}

function RegistrationIframeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#101c16] rounded-2xl shadow-2xl max-w-2xl w-full h-[80vh] relative flex flex-col">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl">&times;</button>
        <iframe
          src="https://getlicense.solanavolumebot.io/register?trial=1"
          title="Registration"
          className="w-full h-full rounded-2xl border-none"
        />
      </div>
    </div>
  );
}

const PricingPlan = ({ 
  name, 
  price, 
  description, 
  features, 
  isPopular = false,
  isTrial = false,
  icon,
  color,
  delay
}: PricingPlanProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { addToCart, openCart } = useCart();
  const { openRegistrationModal } = useLicense();
  
  const handleTrialClick = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      addToCart({
        id: 'free-trial',
        name: 'Solana Volume Bot Free Trial',
        price: 0,
        duration: '7 days',
        icon,
      });
      openCart();
    }, 1500);
  };

  return (
    <div
      className={`${isPopular ? 'z-10' : ''} ${isTrial ? 'lg:col-span-3 md:col-span-2 mb-8' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card className={`
        relative overflow-hidden rounded-2xl 
        border border-gray-800 dark:border-gray-800 
        backdrop-blur-sm
        transition-all duration-500
        flex flex-col h-full
        ${isPopular ? 'shadow-xl shadow-purple-500/10' : 'hover:shadow-lg hover:shadow-purple-500/5'}
        ${isHovered ? 'transform scale-[1.02]' : ''}
        ${isTrial ? 'border-2 border-[#14F195]/50' : ''}
      `}>
        {/* Card Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute top-0 right-0 w-60 h-60 rounded-full opacity-10 filter blur-3xl -translate-y-1/2 translate-x-1/2 bg-gradient-to-r ${color}`}></div>
        </div>

        {/* Badge */}
        {(isPopular || isTrial) && (
          <div className="absolute -top-1 -right-1 z-20">
            <div className={`bg-gradient-to-r ${color} px-6 py-1 text-white font-medium text-xs rounded-bl-lg rounded-tr-xl shadow-lg transform rotate-0 select-none`}>
              {isTrial ? 'FREE TRIAL' : 'MOST POPULAR'}
            </div>
          </div>
        )}

        <CardHeader className={`p-6 border-b border-gray-800/20 relative z-10 ${isTrial || isPopular ? 'bg-gradient-to-b from-card to-transparent' : ''}`}>
          <div className="flex items-center mb-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r ${color} mr-3 shadow-lg`}>
              {icon}
            </div>
            <h3 className="text-xl font-bold">{name}</h3>
          </div>

          <div className="flex items-baseline mb-2">
            {isTrial ? (
              <div className="flex items-center">
                <span className="text-4xl font-bold text-[#14F195]">FREE</span>
                <span className="text-lg ml-1 text-gray-400">for 7 days</span>
              </div>
            ) : (
              <div className="flex items-center">
                <SiSolana className="mr-1 text-[#00FFA3] h-5 w-5" />
                <span className="text-4xl font-bold">{price}</span>
                <span className="text-lg ml-1 text-gray-400">/month</span>
              </div>
            )}
          </div>
          
          <p className="text-gray-400 text-sm">{description}</p>
        </CardHeader>

        <CardContent className={`p-6 flex-grow relative z-10 ${isTrial ? 'md:grid md:grid-cols-2 md:gap-6 lg:grid-cols-3' : ''}`}>
          <ul className={`space-y-4 ${isTrial ? 'md:col-span-2 lg:col-span-3 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4 md:space-y-0' : ''}`}>
            {features.map((feature, index) => (
              <li key={index} className={`flex items-start ${!feature.included ? 'text-gray-300' : ''}`}>
                {feature.included ? (
                  <Check className={`h-5 w-5 ${feature.highlight ? 'text-[#DC1FFF]' : 'text-[#00FFA3]'} mr-3 mt-0.5 flex-shrink-0`} />
                ) : (
                  <div className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                )}
                <span className={feature.highlight ? 'font-medium' : ''}>{feature.text}</span>
              </li>
            ))}
          </ul>
        </CardContent>

        <CardFooter className="p-6 relative z-10">
          <Button
            type="button"
            onClick={handleTrialClick}
            disabled={processing}
            className="w-full h-12 mt-6 bg-gradient-to-r from-[#9945FF] to-[#14F195] text-black font-bold hover:from-[#14F195] hover:to-[#9945FF] hover:text-white transition-colors duration-200"
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              <>
                Get Free License <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>

          {isTrial && processing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="bg-[#101c16] rounded-2xl p-8 shadow-2xl max-w-xs w-full border border-[#14F195]/30 flex flex-col items-center">
                <svg className="animate-spin mb-4" width="40" height="40" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="#14F195" strokeWidth="4" fill="none"/><path className="opacity-75" fill="#9945FF" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                <div className="text-white text-lg font-semibold mb-2">Adding Free Trial to your cart...</div>
                <div className="text-gray-400 text-sm">Please wait</div>
              </div>
            </div>
          )}
          
          {isPopular && !isTrial && (
            <div className="mt-3 text-center">
              <span className="text-xs text-gray-400">7-day free trial available</span>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

const allFeatures = [
  "Automated Activity Distribution",
  "Parallel Transaction Processing",
  "Wallet Rotation System",
  "Custom Activity Patterns",
  "Visibility Analytics Dashboard",
  "Trending Status Monitoring",
  "DEX Platform Integration",
  "Priority Support Access",
  "Advanced Distribution Patterns",
  "Custom Scheduling Options",
  "Activity Controls",
  "Natural Pattern Technology"
];

const pricingPlans = [
  {
    name: "Free Trial",
    price: 0,
    description: "Unlock the full power of Solana Volume Bot for 7 days—absolutely free. Experience every premium feature, maximum scale, and all integrations. Perfect for launching, testing, or boosting your token with no limits.",
    icon: <Moon className="h-5 w-5 text-white" />,
    color: "from-[#9945FF] to-[#14F195]",
    features: [
      { text: "Approx. 0.1 SOL network fee (not charged by us) ≈ $10K volume — depends on market fees.", included: true, highlight: true },
      { text: "10M+ Parallel Wallets", included: true, highlight: true },
      { text: "5M+ Transactions per Day", included: true, highlight: true },
      { text: "Token Holder Boost", included: true },
      { text: "Liquidity Pool Booster", included: true },
      { text: "Full Visibility Dashboard", included: true },
      { text: "Standard Support", included: true },
      { text: "All DEX Integrations (Pump.fun, DEXTools, and more)", included: true },
      { text: "Customizable Activity Patterns & Settings", included: true },
      { text: "Advanced Distribution Controls", included: true },
      { text: "7 Days Free, Full Access", included: true, highlight: true },
      { text: "Moon Shot Analytics", included: true, highlight: true },
    ],
    isPopular: true,
    isTrial: true
  },
  {
    name: "Startup",
    price: 9,
    description: "Ideal for new Solana tokens needing basic trading volume and visibility on key DEX platforms like Pump.Fun and DEXTools. Start your journey with essential features and support.",
    icon: <Rocket className="h-5 w-5 text-white" />,
    color: "from-blue-400 to-blue-600",
    features: [
      { text: "5 Parallel Processors", included: true },
      { text: "15 Rotating Wallets", included: true },
      { text: "10 transactions/minute", included: true },
      { text: "Basic Visibility Dashboard", included: true },
      { text: "Standard Support", included: true },
      { text: "DEXTools Integration", included: true },
      { text: "Custom Activity Patterns", included: true },
      { text: "Advanced Distribution Model", included: true },
      { text: "Distribution Controls", included: true },
      { text: "Enhanced Security Features", included: true },
    ]
  },
  {
    name: "Accelerate",
    price: 19,
    description: "Enhance your token's presence with increased on-chain activity and advanced visibility features across all supported DEX platforms. Perfect for scaling your token's impact.",
    icon: <Flame className="h-5 w-5 text-white" />,
    color: "from-[#00FFA3] to-[#DC1FFF]",
    features: [
      { text: "12 Parallel Processors", included: true, highlight: true },
      { text: "35 Rotating Wallets", included: true, highlight: true },
      { text: "25 transactions/minute", included: true },
      { text: "Advanced Visibility Dashboard", included: true },
      { text: "Priority Support", included: true },
      { text: "All DEX Integrations", included: true },
      { text: "Custom Activity Patterns", included: true, highlight: true },
      { text: "Distribution Controls", included: true },
      { text: "Advanced Distribution Model", included: true },
      { text: "Real-time Analytics", included: true },
      { text: "Dedicated Account Manager", included: true },
    ]
  },
  {
    name: "Dominate",
    price: 49,
    description: "Achieve maximum token discoverability and trending status on Pump.Fun, DEXTools, and Dexscreener with our most powerful volume bot plan. Dominate the market with unparalleled features.",
    icon: <Crown className="h-5 w-5 text-white" />,
    color: "from-amber-400 to-orange-600",
    features: [
      { text: "25 Parallel Processors", included: true },
      { text: "50+ Rotating Wallets", included: true },
      { text: "40 transactions/minute", included: true },
      { text: "Premium Visibility Dashboard", included: true },
      { text: "24/7 VIP Support", included: true },
      { text: "All DEX Integrations", included: true },
      { text: "Custom Activity Patterns", included: true },
      { text: "Distribution Controls", included: true },
      { text: "Advanced Distribution Model", included: true, highlight: true },
      { text: "Exclusive Market Insights", included: true },
      { text: "Unlimited Access to New Features", included: true },
    ]
  }
];

export function PricingSection() {
  const [isMonthly, setIsMonthly] = useState(true);

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-40 -right-40 w-80 h-80 bg-[#DC1FFF] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#00FFA3] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center justify-center text-center mb-16">
          <div className="inline-flex items-center px-3 py-1 mb-4 rounded-full bg-card/40 backdrop-blur-sm border border-gray-800 shadow-lg">
            <Diamond className="mr-2 h-4 w-4 text-[#00FFA3]" />
            <span className="text-xs font-medium">Flexible Plans</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#00FFA3] via-blue-400 to-[#DC1FFF]">
            Solana Volume Bot Pricing Plans for Token Visibility
          </h2>
          
          <p className="max-w-2xl text-gray-400 md:text-lg mb-10">
            Select the perfect <span className="text-[#00FFA3]">Solana Volume Bot</span> or <span className="text-[#00FFA3]">SOL Volume Bot</span> plan to boost your <span className="text-[#00FFA3]">token's visibility</span> and <span className="text-[#00FFA3]">trading volume</span> on key platforms like <span className="text-[#00FFA3]">Pump.fun</span>, <span className="text-[#00FFA3]">DEXTools</span>, and <span className="text-[#00FFA3]">Dexscreener</span>. Our plans are designed to help your token achieve <span className="text-[#00FFA3]">trending status</span> and attract genuine interest.
          </p>

          {/* Toggle for Monthly/Annual */}
          <div className="flex items-center space-x-3 bg-card/60 p-1 rounded-full border border-gray-800 mb-5">
            <button 
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${isMonthly ? 'bg-[#00FFA3]/20 text-[#00FFA3]' : 'text-gray-400 hover:text-gray-300'}`}
              onClick={() => setIsMonthly(true)}
            >
              Monthly
            </button>
            <button 
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!isMonthly ? 'bg-[#00FFA3]/20 text-[#00FFA3]' : 'text-gray-400 hover:text-gray-300'}`}
              onClick={() => setIsMonthly(false)}
            >
              Annual <span className="text-xs bg-[#6B21A8] text-white px-2 py-0.5 rounded-full ml-1">Save 20%</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-10 max-w-6xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <PricingPlan
              key={index}
              name={plan.name}
              price={isMonthly ? plan.price : Math.round(plan.price * 0.8 * 12)}
              description={plan.description}
              features={plan.features}
              isPopular={plan.isPopular}
              isTrial={plan.isTrial}
              icon={plan.icon}
              color={plan.color}
              delay={index}
            />
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-gray-800 bg-card/60 backdrop-blur-sm">
            <HelpCircle className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-300">Need a custom plan? <a href="#contact" className="text-[#00FFA3] hover:underline">Contact us</a></span>
          </div>
          
          <div className="mt-8">
            <p className="max-w-3xl mx-auto text-sm text-gray-300 mb-4">
              All plans include: Secure <span className="text-[#00FFA3]">Solana trading volume</span> generation through advanced <span className="text-[#00FFA3]">on-chain activity distribution</span>, mimicking <span className="text-[#00FFA3]">natural trading patterns</span> for optimal <span className="text-[#00FFA3]">DEX visibility</span>. Enjoy automatic renewal,
              and access to our dedicated support team. Cancel anytime, no questions asked.
            </p>
            
            <div className="max-w-3xl mx-auto bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-8 text-amber-100">
              <p className="text-center text-sm">
 <strong>Notice:</strong> <span className="text-amber-300">Solana Volume Bot</span> is a technical tool designed to enhance <span className="text-amber-300">token visibility</span> through sophisticated <span className="text-amber-300">on-chain activity distribution</span>. 
                It is not a trading service and does not guarantee financial outcomes. Results depend on your token's parameters 
                and overall market conditions. Please use responsibly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
